import React from 'react';
import PropTypes from 'prop-types';
import { createMemoStyles, defaultBaseline, uniqueId, safeRest } from '@wds/utils';
import { useTheme } from 'react-jss';
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}
var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    type = theme.type,
    breakpoints = theme.breakpoints;
  return {
    wrenchPageHeader: function wrenchPageHeader(_ref) {
      var centered = _ref.centered;
      return _objectSpread(_objectSpread({}, defaultBaseline), {}, {
        width: '100%',
        padding: [spacing[5], spacing[6]],
        fontFamily: type.fontFamily.base,
        '-webkit-font-smoothing': 'antialiased',
        textAlign: centered ? 'center' : 'left',
        backgroundColor: palette.background.light,
        display: centered ? 'block' : 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'space-between'
      });
    },
    wrenchPageHeaderText: function wrenchPageHeaderText(_ref2) {
      var centered = _ref2.centered;
      var centeredStyles = centered ? {
        display: 'block'
      } : {};
      return _objectSpread(_objectSpread({
        flexBasis: '50%',
        flexGrow: 1,
        marginRight: '1em'
      }, centeredStyles), {}, _defineProperty({}, "@media (min-width: ".concat(breakpoints.lg, "px)"), {
        flexBasis: '30%'
      }));
    },
    wrenchPageHeaderAddOn: function wrenchPageHeaderAddOn(_ref3) {
      var centered = _ref3.centered;
      var centeredStyles = centered ? {
        width: '100%',
        display: 'block',
        textAlign: 'center',
        whiteSpace: 'normal'
      } : {};
      return _objectSpread({
        flexGrow: 0,
        flexShrink: 1,
        textAlign: 'right',
        '& [class*="wrenchButton"]': {
          margin: [spacing[1], spacing[1], spacing[1], 0]
        }
      }, centeredStyles);
    },
    wrenchPageHeaderTitle: function wrenchPageHeaderTitle() {
      return _objectSpread(_objectSpread({}, defaultBaseline), {}, {
        margin: 0,
        lineHeight: 1.5,
        fontSize: type.fontSize.sectionTitle,
        fontWeight: type.fontWeight.light
      });
    },
    wrenchPageHeaderSubtitle: function wrenchPageHeaderSubtitle() {
      return _objectSpread(_objectSpread({}, defaultBaseline), {}, {
        display: 'block',
        marginBottom: spacing[2],
        color: palette.text.darkMuted,
        fontSize: type.fontSize.cardTitle
      });
    }
  };
});
var _excluded = ["addOn", "centered", "subTitle", "title"];
var PageHeader = function PageHeader(_ref) {
  var addOn = _ref.addOn,
    centered = _ref.centered,
    subTitle = _ref.subTitle,
    title = _ref.title,
    other = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var classes = useStyles({
    centered: centered,
    theme: theme
  });
  var headerId = uniqueId('page-header-');
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: classes.wrenchPageHeader
  }, safeRest(other)), /*#__PURE__*/React.createElement("div", {
    className: classes.wrenchPageHeaderText,
    "aria-labelledby": headerId
  }, /*#__PURE__*/React.createElement("h1", {
    className: classes.wrenchPageHeaderTitle,
    id: headerId
  }, title), subTitle ? /*#__PURE__*/React.createElement("span", {
    className: classes.wrenchPageHeaderSubtitle
  }, subTitle) : null), addOn ? /*#__PURE__*/React.createElement("div", {
    className: classes.wrenchPageHeaderAddOn
  }, addOn) : null);
};
PageHeader.displayName = "PageHeader";
PageHeader.propTypes = {
  /**
   * Define page header title
   */
  title: PropTypes.node.isRequired,
  /**
   * Define page header sub title
   */
  subTitle: PropTypes.node,
  /**
   * Define add-on options like buttons or input
   */
  addOn: PropTypes.node,
  /**
   * If true, content will be centered
   */
  centered: PropTypes.bool
};
PageHeader.defaultProps = {
  addOn: undefined,
  centered: false,
  subTitle: undefined
};
PageHeader.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "PageHeader",
  "props": {
    "addOn": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Define add-on options like buttons or input"
    },
    "centered": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If true, content will be centered"
    },
    "subTitle": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Define page header sub title"
    },
    "title": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Define page header title"
    }
  }
};
export { PageHeader };